import type { Granularity, TimeRangeDef, TimeRangePreset } from '../Settings/reportDsl';
import { resolveReportsPresetBounds } from './reportsTimeRange';

/** Grid row height (px) — one `layout.h` unit on the Reports dashboard. */
export const REPORT_PANEL_ROW_PX = 80;

export const panelMinHeightPx = (layoutH?: number): number =>
  Math.max(3, layoutH ?? 4) * REPORT_PANEL_ROW_PX;

const HOUR_PRESETS: ReadonlySet<TimeRangePreset> = new Set([
  'last_1_hour',
  'last_6_hours',
  'last_12_hours',
  'last_24_hours',
  'today',
  'yesterday',
]);

const DAY_PRESETS: ReadonlySet<TimeRangePreset> = new Set([
  'last_7_days',
  'this_week',
  'last_30_days',
  'last_90_days',
  'last_180_days',
  'this_month',
]);

const MONTH_PRESETS: ReadonlySet<TimeRangePreset> = new Set([
  'this_year',
  'last_365_days',
]);

/** Compact x-axis caption for time-bucket charts (avoids per-tick date clutter). */
export const formatTimeBucketAxisUnitLabel = (granularity?: Granularity): string => {
  switch (granularity) {
    case 'minute':
      return 'Minutes ->';
    case 'hour':
      return 'Hours ->';
    case 'month':
      return 'Months ->';
    case 'week':
    case 'day':
    default:
      return 'Days ->';
  }
};

/** Pick ClickHouse bucket granularity from the active dashboard time window. */
export const deriveChartGranularity = (timeRange?: TimeRangeDef): Granularity | undefined => {
  if (!timeRange) return 'day';

  if (timeRange.preset) {
    if (timeRange.preset === 'last_1_hour') return 'minute';
    if (HOUR_PRESETS.has(timeRange.preset)) return 'hour';
    if (DAY_PRESETS.has(timeRange.preset)) return 'day';
    if (MONTH_PRESETS.has(timeRange.preset)) return 'month';
    return 'day';
  }

  if (timeRange.start && timeRange.end) {
    const startMs = parseChDateTimeMs(timeRange.start);
    const endMs = parseChDateTimeMs(timeRange.end);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 'day';
    const spanMs = endMs - startMs;
    const hour = 60 * 60 * 1000;
    const day = 24 * hour;
    if (spanMs <= 2 * day) return 'hour';
    if (spanMs <= 120 * day) return 'day';
    return 'month';
  }

  return timeRange.granularity || 'day';
};

export const mergeReportTimeRange = (
  base: TimeRangeDef | undefined,
  override: TimeRangeDef | undefined,
): TimeRangeDef | undefined => {
  if (!override) return base;
  if (!base) return override;

  const merged: TimeRangeDef = { ...base };

  if (override.start || override.end) {
    merged.start = override.start;
    merged.end = override.end;
    merged.preset = undefined;
  } else if (override.preset) {
    merged.preset = override.preset;
    merged.start = undefined;
    merged.end = undefined;
  }

  if (override.granularity !== undefined) {
    merged.granularity = override.granularity;
  }

  return merged;
};

/** Effective range for chart bucketing (toolbar override + report DSL). */
export const resolveEffectiveChartTimeRange = (
  base: TimeRangeDef | undefined,
  override: TimeRangeDef | undefined,
  autoGranularity = true,
): TimeRangeDef | undefined => {
  const merged = mergeReportTimeRange(base, override);
  if (!merged) return undefined;
  if (autoGranularity) {
    return { ...merged, granularity: deriveChartGranularity(merged) };
  }
  return merged;
};

export const resolveChartRangeBoundsMs = (timeRange: TimeRangeDef): { start: number; end: number } => {
  if (timeRange.preset) {
    return resolveReportsPresetBounds(timeRange.preset);
  }
  if (timeRange.start && timeRange.end) {
    return {
      start: parseChDateTimeMs(timeRange.start),
      end: parseChDateTimeMs(timeRange.end),
    };
  }
  return resolveReportsPresetBounds('last_30_days');
};

export const parseChDateTimeMs = (raw: string): number => {
  const str = String(raw);
  const isoish = /^\d{4}-\d{2}-\d{2} /.test(str) ? `${str.replace(' ', 'T')}Z` : str;
  const d = new Date(isoish);
  return d.getTime();
};

/** Format a Date to match ClickHouse JSON bucket strings (`YYYY-MM-DD HH:mm:ss`). */
export const formatChBucketString = (date: Date, granularity: Granularity): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = date.getUTCFullYear();
  const mo = pad(date.getUTCMonth() + 1);
  const d = pad(date.getUTCDate());
  const h = pad(date.getUTCHours());
  const mi = pad(date.getUTCMinutes());
  switch (granularity) {
    case 'minute':
      return `${y}-${mo}-${d} ${h}:${mi}:00`;
    case 'hour':
      return `${y}-${mo}-${d} ${h}:00:00`;
    case 'month':
      return `${y}-${mo}-01 00:00:00`;
    case 'week':
    case 'day':
    default:
      return `${y}-${mo}-${d} 00:00:00`;
  }
};

const truncateUtc = (date: Date, granularity: Granularity): Date => {
  const d = new Date(date);
  switch (granularity) {
    case 'minute':
      d.setUTCSeconds(0, 0);
      break;
    case 'hour':
      d.setUTCMinutes(0, 0, 0);
      break;
    case 'month':
      d.setUTCDate(1);
      d.setUTCHours(0, 0, 0, 0);
      break;
    case 'week': {
      const day = d.getUTCDay();
      d.setUTCDate(d.getUTCDate() - day);
      d.setUTCHours(0, 0, 0, 0);
      break;
    }
    case 'day':
    default:
      d.setUTCHours(0, 0, 0, 0);
      break;
  }
  return d;
};

const stepUtc = (date: Date, granularity: Granularity): Date => {
  const d = new Date(date);
  switch (granularity) {
    case 'minute':
      d.setUTCMinutes(d.getUTCMinutes() + 1);
      break;
    case 'hour':
      d.setUTCHours(d.getUTCHours() + 1);
      break;
    case 'week':
      d.setUTCDate(d.getUTCDate() + 7);
      break;
    case 'month':
      d.setUTCMonth(d.getUTCMonth() + 1);
      break;
    case 'day':
    default:
      d.setUTCDate(d.getUTCDate() + 1);
      break;
  }
  return d;
};

const normalizeBucketKey = (raw: unknown): string => {
  const str = String(raw ?? '');
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(str)) {
    return str.slice(0, 19);
  }
  return str;
};

/** Insert zero-value buckets so the x-axis spans the full selected range. */
export const fillTimeSeriesBuckets = (
  data: Record<string, unknown>[],
  xKey: string,
  metricLabels: string[],
  timeRange: TimeRangeDef,
  granularity: Granularity,
): Record<string, unknown>[] => {
  if (xKey !== 'bucket' || !timeRange?.granularity) return data;

  const { start, end } = resolveChartRangeBoundsMs(timeRange);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return data;

  const byBucket = new Map<string, Record<string, unknown>>();
  for (const row of data) {
    byBucket.set(normalizeBucketKey(row[xKey]), row);
  }

  const zeroMetrics = Object.fromEntries(metricLabels.map((label) => [label, 0]));
  const result: Record<string, unknown>[] = [];

  let cursor = truncateUtc(new Date(start), granularity);
  const endDate = new Date(end);
  let guard = 0;
  const maxPoints = 4000;

  while (cursor.getTime() <= endDate.getTime() && guard < maxPoints) {
    const bucketStr = formatChBucketString(cursor, granularity);
    const existing = byBucket.get(bucketStr);
    if (existing) {
      result.push(existing);
    } else {
      result.push({ [xKey]: bucketStr, ...zeroMetrics });
    }
    cursor = stepUtc(cursor, granularity);
    guard += 1;
  }

  return result.length ? result : data;
};
